type StyleHost = Document | ShadowRoot;

/**
 * Creates a constructed stylesheet from a readonly one
 */
const constructStylesheet = (sheet: CSSStyleSheet): CSSStyleSheet => {
  const constructed = new CSSStyleSheet();
  try {
    constructed.replaceSync(
      Array.from(sheet.cssRules)
        .map(rule => {
          try {
            return rule.cssText;
          } catch {
            return ''; // Skip malformed rules
          }
        })
        .filter(Boolean)
        .join('\n')
    );
  } catch (error) {
    console.error('Failed to construct stylesheet:', error);
  }
  return constructed;
};

const processStylesheet = (
  cache: Map<string, CSSStyleSheet>,
  link: HTMLLinkElement
): boolean => {
  const {href, sheet} = link;

  if (!sheet || cache.has(href)) return false;

  const constructed = constructStylesheet(sheet);
  cache.set(href, constructed);

  return true;
};

const injectStylesheet = (target: StyleHost, sheet: CSSStyleSheet): void => {
  try {
    if (!('adoptedStyleSheets' in target)) return;
    const current = target.adoptedStyleSheets;
    if (current.includes(sheet)) return;
    target.adoptedStyleSheets = [...current, sheet];
  } catch (error) {
    console.error('Failed to inject stylesheet:', error);
  }
};

const injectAllStylesheets = (
  cache: Map<string, CSSStyleSheet>,
  target: StyleHost
): void => {
  cache.forEach(sheet => injectStylesheet(target, sheet));
};

/**
 * Manages stylesheet injection into adoptedStyleSheets
 */
export class StylesheetManager {
  private observer: MutationObserver | null = null;
  private readonly selector: string;
  private readonly stylesheetCache: Map<string, CSSStyleSheet>;
  private readonly processedElements: Set<Element>;
  private readonly pendingStylesheets = new Set<HTMLLinkElement>();

  constructor(selector = 'data-vanilla-extract') {
    this.selector = selector;
    this.stylesheetCache = new Map<string, CSSStyleSheet>();
    this.processedElements = new Set<Element>();
  }

  /**
   * Process all available stylesheets from the document into the cache
   */
  private processAvailableStylesheets(): void {
    this.processStylesheets([
      ...document.querySelectorAll<HTMLLinkElement>(`link[${this.selector}]`),
    ]);
  }

  private readonly handleStylesheetLoad = (event: Event): void => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLLinkElement)) return;
    this.pendingStylesheets.delete(link);
    if (processStylesheet(this.stylesheetCache, link)) {
      this.injectCachedStylesIntoAllHosts();
    }
  };

  private processStylesheets(links: HTMLLinkElement[]): void {
    let hasNewSheets = false;
    links.forEach(link => {
      if (link.sheet) {
        if (processStylesheet(this.stylesheetCache, link)) {
          hasNewSheets = true;
        }
      } else if (!this.pendingStylesheets.has(link)) {
        this.pendingStylesheets.add(link);
        link.addEventListener('load', this.handleStylesheetLoad, {once: true});
      }
    });
    if (hasNewSheets) this.injectCachedStylesIntoAllHosts();
  }

  /**
   * Inject all cached styles into document and already-processed shadow roots
   */
  private injectCachedStylesIntoAllHosts(): void {
    injectAllStylesheets(this.stylesheetCache, document);
    this.processedElements.forEach(element => {
      const root = element.shadowRoot;
      if (!root) return;
      injectAllStylesheets(this.stylesheetCache, root);
    });
  }

  public init(): void {
    if (this.observer) return;
    // Initialize cache with existing stylesheets
    this.processAvailableStylesheets();

    // Ensure initial adoption without waiting for mutations
    this.injectCachedStylesIntoAllHosts();

    // Set up mutation observer for new stylesheets
    this.observer = new MutationObserver((mutations: MutationRecord[]) => {
      const newSheets = mutations
        .filter(({type}) => type === 'childList')
        .flatMap(({addedNodes}) => Array.from(addedNodes))
        .filter(
          (node): node is HTMLLinkElement =>
            node instanceof HTMLLinkElement && node.hasAttribute(this.selector)
        );

      this.processStylesheets(newSheets);
    });

    // Observe the document head for new stylesheets
    this.observer.observe(document.head, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Inject stylesheets into a target element
   */
  public injectIntoElement(element: Element): void {
    this.init();
    if (this.processedElements.has(element)) return;

    const target = element.shadowRoot ?? document;

    // If cache is empty, try processing any stylesheets again
    if (this.stylesheetCache.size === 0) {
      this.processAvailableStylesheets();
    }

    this.stylesheetCache.forEach(sheet => injectStylesheet(target, sheet));
    this.processedElements.add(element);
  }

  private removeOwnedStylesheets(target: StyleHost): void {
    if (!('adoptedStyleSheets' in target)) return;
    const owned = new Set(this.stylesheetCache.values());
    target.adoptedStyleSheets = target.adoptedStyleSheets.filter(
      sheet => !owned.has(sheet)
    );
  }

  /** Unregister one host without interrupting other mounted islands. */
  public removeElement(element: Element): void {
    if (element.shadowRoot) this.removeOwnedStylesheets(element.shadowRoot);
    this.processedElements.delete(element);
    if (this.processedElements.size === 0) this.disconnect();
  }

  /**
   * Inject all cached stylesheets into document
   */
  public injectIntoDocument(): void {
    injectAllStylesheets(this.stylesheetCache, document);
  }

  /**
   * Clean up resources
   */
  public disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.pendingStylesheets.forEach(link =>
      link.removeEventListener('load', this.handleStylesheetLoad)
    );
    this.pendingStylesheets.clear();
    this.processedElements.forEach(element => {
      if (element.shadowRoot) this.removeOwnedStylesheets(element.shadowRoot);
    });
    this.removeOwnedStylesheets(document);
    this.processedElements.clear();
    this.stylesheetCache.clear();
  }
}

export const createStylesheetManager = (
  selector?: string
): StylesheetManager => {
  return new StylesheetManager(selector);
};
